<template>
  <div class="calendar-days">
    <div
      v-for="(date, keyDate) in calendar.date()"
      :key="keyDate"
      class="calendar-days__box"
      :class="{ 'calendar-days__tooltip': asRange && date.duration() }"
      :data-tooltip="`${date.duration()}`"
    >
      <span
        v-if="isBetweenRange(date) || date.hovered()"
        class="calendar-days__range-line"
        :class="betweenRangeClasses(date)"
      ></span>

      <button
        type="button"
        class="calendar-days__day-btn"
        :class="[
          datepickerClasses(date)
        ]"
        :disabled="date.disabled || date.inRange()"
        @click="$emit('update:date', date, asPrevOrNext)"
        @mouseenter="atMouseOver(date)"
        @focusin="atMouseOver(date)"
        v-text="date.date()"
        :data-date="date.toDate()"
      ></button>
    </div>
  </div>
</template>

<script>
import { defineComponent, inject } from 'vue';

export default defineComponent({
  props: {
    asPrevOrNext: Boolean,
    calendar: Object,
    weeks: Array,
    asRange: Boolean
  },
  inheritAttrs: false,
  emits: ['update:date'],
  setup() {
    const isBetweenRange = inject('isBetweenRange');
    const betweenRangeClasses = inject('betweenRangeClasses');
    const datepickerClasses = inject('datepickerClasses');
    const atMouseOver = inject('atMouseOver');

    return {
      isBetweenRange,
      betweenRangeClasses,
      datepickerClasses,
      atMouseOver
    };
  }
});
</script>

<style scoped lang="scss">
.calendar-days {
  display: grid;
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
  grid-template-columns: repeat(7, minmax(0, 4rem));

  &__transition {
    &__enter-from-class {
      opacity: 0;
    }

    &__enter-to-class {
      opacity: 1;
    }

    &__enter-active-class {
      transition-property: opacity;
      transition-timing-function: ease-out;
      transition-duration: 300ms;
    }

    &__leave-active-class {
      transition-property: opacity;
      transition-timing-function: ease-in;

      &--duration-200 {
        transition-duration: 200ms;
      }

      &--duration-150 {
        transition-duration: 150ms;
      }
    }

    &__leave-from-class {
      opacity: 1;
    }

    &__leave-to-class {
      opacity: 0;
    }
  }

  &__box {
    position: relative;
  }

  &__range-line {
    position: absolute;
    background: rgba(209, 250, 229, var(--bg-opacity));

    &--rounded-right {
      border-top-right-radius: 9999px;
      border-bottom-right-radius: 9999px;
    }

    &--rounded-left {
      border-top-left-radius: 9999px;
      border-bottom-left-radius: 9999px;
    }

    &--inset-zero {
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
    }
  }

  &__day-btn {
    display: flex;
    position: relative;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 4rem;
    font-size: 0.75rem;
    line-height: 1rem;
    color: white;
    background: transparent;

    @media (min-width: 1024px) {
      width: 100%;
      height: 4rem;
      font-size: 1.4rem;
    }

    &:hover {
      cursor: pointer;
    }

    &--today {
      border-radius: 9999px;
      font-weight: 600;
    }

    &--between-range {
      font-weight: 500;
      background-color: rgba(91, 162, 255, 0.1);
      background-image: none;
    }

    &--not-between-range {
      border-radius: 9999px;
      font-weight: 500;
    }

    &--end-after-start {
      border-top-left-radius: 9999px;
      border-bottom-left-radius: 9999px;
      font-weight: 700;
      background-color: #5ba2ff;

      &:disabled {
        cursor: not-allowed;
      }

      &:hover {
        cursor: pointer;
        border-top-left-radius: 9999px;
        border-bottom-left-radius: 9999px;
      }
    }

    &--start-after-end {
      border-top-right-radius: 9999px;
      border-bottom-right-radius: 9999px;
      font-weight: 700;
      background-color: #5ba2ff;

      &:disabled {
        cursor: not-allowed;
      }

      &:hover {
        cursor: pointer;
        border-top-right-radius: 9999px;
        border-bottom-right-radius: 9999px;
      }
    }

    &--start-same-as-end {
      border-radius: 9999px;
      font-weight: 700;
      background-color: #5ba2ff;

      &:disabled {
        cursor: not-allowed;
      }

      &:hover {
        cursor: pointer;
        border-radius: 9999px;
        background-color: darken(#5ba2ff, 10%);
      }
    }

    &--off {
      font-weight: 300;
      opacity: 0.38;
      background: transparent;

      &:disabled {
        cursor: not-allowed;
      }
    }

    &--disabled {
      border-radius: 9999px;
      font-weight: 300;
      opacity: 0.38;
      background: transparent;
      &:disabled {
        cursor: not-allowed;
      }
    }

  }
}
</style>